/* globals gsap */

import './registerBlur';

import {ScrollTrigger} from 'gsap/ScrollTrigger';

import {debounce, getDistance, lerp} from '../../../static/js/lib/utilities';
import PixiScene from './pixi-scene';
import SectionSelector from './sectionSelector';
import TestimonialScene from './testimonialScene';

// constants
const SCRUB = 0.5;
const TEAM_MEMBER_DISTANCE = 500;
const TEAM_SCROLL_DISTANCE = 2000;
const INVESTMENTS_ITEM_DISTANCE = 400;
const INVESTMENTS_SCROLL_DISTANCE = 2000;
const TESTIMONIAL_ITEM_DISTANCE = 800;
const TESTIMONIAL_SCROLL_DISTANCE = 1500;
const MEDIA_ITEM_DISTANCE = 24; // in vh
const MEDIA_SCROLL_DISTANCE = 150; // in vh
const VISION_SCALE_RADIUS = 0.75;
const GRAY_5 = '#323232';
const GRAY_2 = '#C1C1C1';

export default class Homepage {
  constructor() {
    // initial properties
    this.animations = [];
    this.resizeAnimations = [];
    this.isOrbit3Animating = true;
    // bind
    this.animateOrbit3 = this.animateOrbit3.bind(this);

    // elements
    this.$blackCircle = document.querySelector('.homepageBlackCircle');
    this.$heroMedia = document.querySelector('.homepageHeroMedia');
    this.$investmentsButton = document.querySelector('.homepageInvestmentsButton');
    this.$investmentItemsTop = document.querySelectorAll('.homepageInvestmentsList__item--top');
    this.$investmentItemsBottom = document.querySelectorAll('.homepageInvestmentsList__item--bottom');
    this.$investmentsTitle = document.querySelector('.homepageInvestmentsTitle');
    const $investmentsProgressCircle1 = document.querySelector('.homepageProgressCircle--investments1');
    const $investmentsProgressCircle2 = document.querySelector('.homepageProgressCircle--investments2');
    const $investmentsProgressCircle3 = document.querySelector('.homepageProgressCircle--investments3');
    this.$keyhole = document.querySelector('.homepageKeyhole');
    this.$mediaCards = document.querySelectorAll('.homepageMediaCard');
    this.$mediaButton = document.querySelector('.homepageMediaButton');
    this.$minimapOuter = document.querySelector('.minimapOuter');
    this.$orbit1 = document.querySelector('.homepageOrbit--1');
    this.$orbit2 = document.querySelector('.homepageOrbit--2');
    this.$orbit3 = document.querySelector('.homepageOrbit--3');
    this.$ruler = document.querySelector('.homepageRuler');
    this.$scrollSectionInvestments = document.querySelector('.homepageScrollSection--investments');
    this.$shapesSvg = document.querySelector('.homepageShapesSvg');
    this.$teamInterstitialTitle = document.querySelector('.homepageTeamInterstitialTitle');
    this.$teamContainer = document.querySelector('.homepageTeamContainer');
    this.$teamProgressCircle = document.querySelector('.homepageProgressCircle--team');
    this.$teamMembers = document.querySelectorAll('.homepageTeamMember');
    this.$testimonials = document.querySelectorAll('.homepageTestimonial');
    this.$visionProgressCircle = document.querySelector('.homepageProgressCircle--vision');
    this.$visionText = document.querySelector('.homepageVisionText');
    this.$whiteCircle = document.querySelector('.homepageWhiteCircle');

    this.$investmentsProgressCircles = [
      $investmentsProgressCircle1,
      $investmentsProgressCircle2,
      $investmentsProgressCircle3,
    ];

    // Scroll Section Height
    const $scrollSectionTestimonial = document.querySelector('.homepageScrollSection--testimonial');
    const $scrollSectionMedia = document.querySelector('.homepageScrollSection--media');
    const $scrollSectionTeam = document.querySelector('.homepageScrollSection--team');
    const teamScrollHeight = TEAM_SCROLL_DISTANCE + TEAM_MEMBER_DISTANCE * this.$teamMembers.length;
    const investmentsScrollHeight = INVESTMENTS_SCROLL_DISTANCE + INVESTMENTS_ITEM_DISTANCE * this.$investmentItemsBottom.length;
    const totalTestimonialScrollHeight = TESTIMONIAL_SCROLL_DISTANCE + TESTIMONIAL_ITEM_DISTANCE * (this.$testimonials.length - 1);
    const totalMediaScrollHeight = MEDIA_SCROLL_DISTANCE + MEDIA_ITEM_DISTANCE * this.$mediaCards.length;
    $scrollSectionTeam.style.height = `calc(30vh + ${teamScrollHeight}px)`;
    this.$scrollSectionInvestments.style.height = `calc(15vh + ${investmentsScrollHeight}px)`;
    $scrollSectionTestimonial.style.height = `calc(${totalTestimonialScrollHeight}px)`;
    $scrollSectionMedia.style.height = `calc(50vh + ${totalMediaScrollHeight}vh)`;

    // get initial measurements
    this.updateMeasurements();
    this.orbit3Theta = this.isMinQueryM ? Math.PI : -1;
    // events
    this.onResize = debounce(this.onResize.bind(this));
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('resize', this.onResize);
    this.onScroll(); // trigger initially to set minimap

    // -------------------------- features -------------------------- //

    // init pixiScene & set initial value off-screen
    const $pixiText = document.querySelector('[data-js="PixiScene"]');
    this.pixiScene = new PixiScene($pixiText);
    this.pixiScene.setTextX(5000);

    this.sectionSelector = new SectionSelector();
    this.testimonialScene = new TestimonialScene();

    this.createAnimations();
  }

  startOrbit3Animation() {
    if (!this.isMinQueryM) return; // desktop only
    this.isOrbit3Animating = true;
    this.animateOrbit3();
  }

  stopOrbit3Animation() {
    this.isOrbit3Animating = false;
  }

  /**
   * @return {Object} - x, y
   */
  getOrbit3Position() {
    const scale = ( this.isMinQueryM ? 0.8 : 0.6 ) * this.vmin;
    // numbers here are based around orbit2 start position
    const centerX = this.isMinQueryM ? this.windowWidth * 0.25 : 0;
    const centerY = this.isMinQueryM ? this.windowHeight * -0.2 : 0;
    return {
      x: Math.cos(this.orbit3Theta) * scale + centerX,
      y: Math.sin(this.orbit3Theta) * scale + centerY,
    };
  }

  animateOrbit3() {
    if (!this.isOrbit3Animating) return;

    this.orbit3Theta -= 0.0015;
    const position = this.getOrbit3Position();
    gsap.set(this.$orbit3, position);
    requestAnimationFrame(this.animateOrbit3);
  }

  /**
   * enormous function to animations can be killed and re-created for loop-back-to-top
   */
  createAnimations() {
    // de-construct all these for better linting
    const {
      $blackCircle,
      $heroMedia,
      $investmentsButton,
      $investmentsTitle,
      $investmentsProgressCircles,
      $keyhole,
      $mediaButton,
      $orbit1,
      $orbit2,
      $orbit3,
      $scrollSectionInvestments,
      $teamInterstitialTitle,
      $teamContainer,
      $teamProgressCircle,
      $testimonials,
      $visionProgressCircle,
      $visionText,
      $whiteCircle,
      animations,
      diagonalScale,
      isMinQueryM,
      pixiScene,
      testimonialScene,
      windowDiagonal,
      windowWidth,
      windowHeight,
    } = this;
    // proxy this for use within GSAP method
    const _this = this;

    // add class so all sections are visible while initializing
    document.body.classList.add('isInitializing');

    // HACK set orbit3s position to 0 to properly
    // set data-svg-origin. Fixes GSAP bug
    gsap.set($orbit3, {x: 0, y: 0});

    // -------------------------- initial values -------------------------- //

    const orbit1InitValues = {
      scaleRadius: 3,
      x: isMinQueryM ? '15vw' : '50vw',
      y: isMinQueryM ? '-75vh' : '-30vh',
      stroke: GRAY_5,
    };

    const orbit2InitValues = {
      scaleRadius: 2,
      x: isMinQueryM ? '25vw' : '50vw',
      y: isMinQueryM ? '-20vh' : '-5vh',
      stroke: GRAY_5,
    };

    const orbit3InitValues = {
      scaleRadius: 0.12,
      stroke: GRAY_5,
    };

    const initialBlackCircleValues = this.getCircleValues({
      x: '0vw',
      y: '100vh',
      blur: 0,
      scaleRadius: 1,
    });

    const initialWhiteCircleValues = this.getCircleValues({
      opacity: 0,
      scaleRadius: 0.01,
      x: '0vw',
      y: '0vh',
    });

    // -------------------------- launch -------------------------- //

    const launchTopTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.homepageScrollSection--launch',
        scrub: SCRUB,
        start: 'top top',
        end: 'bottom top',
      },
      onLeave() {
        $keyhole.style.display = 'none';
      },
      onEnterBack() {
        $keyhole.style.display = 'flex';
      },
    });
    animations.push(launchTopTimeline);

    const initialHeroMediaValues = {
      scale: 0.5,
      x: 0,
      y: 0,
    };

    launchTopTimeline.fromTo($heroMedia, initialHeroMediaValues, {
      scale: 1,
      x: this.isMinQueryM ? '15vw' : 0,
      y: this.isMinQueryM ? 0 : '-10vh',
    }, 0);

    const initialKeyholeValues = {
      scale: 1,
      x: 0,
      y: 0,
      opacity: 1,
    };
    const lastKeyholeValues = {
      scale: isMinQueryM ? 12 : 3,
      opacity: isMinQueryM ? 1 : 0,
      x: 0,
      y: 0,
    };

    launchTopTimeline.fromTo($keyhole, initialKeyholeValues, lastKeyholeValues, 0);

    // -------------------------- Hero -------------------------- //

    let $heroTextElements;
    if (isMinQueryM) {
      // select multiple elements on desktop
      $heroTextElements = [
        ...document.querySelectorAll('.homepageHero__text > *'),
        // filter out empty element
      ].filter(($element) => $element.textContent);
    } else {
      // select single element on mobile
      $heroTextElements = [document.querySelectorAll('.homepageHero__text')];
    }

    $heroTextElements.forEach(($element, i) => {
      animations.push(gsap.fromTo($element, {
        y: '10vh',
        opacity: 0,
      }, {
        scrollTrigger: {
          trigger: '.homepageScrollSection--hero',
          scrub: SCRUB,
          start: `top bottom-=${i * 15}%`,
          end: '+=50%',
        },
        y: 0,
        opacity: 1,
      }, 0));
    });

    const outroHeroTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.homepageScrollSection--hero',
        scrub: SCRUB,
        start: 'bottom bottom',
        end: 'bottom top',
      },
    });
    animations.push(outroHeroTimeline);

    if (isMinQueryM) {
      outroHeroTimeline.fromTo($orbit1, {}, this.getCircleValues({
        scaleRadius: 2.75,
        x: isMinQueryM ? '10vh' : '50vw',
        y: isMinQueryM ? '-60vh' : '0vh',
      }), 0);
      outroHeroTimeline.fromTo($orbit2, {}, this.getCircleValues({
        scaleRadius: 1.75,
        x: isMinQueryM ? '15vw' : '40vw',
        y: '0vh',
      }), 0);
    }

    const lastHeroMediaValues = {
      x: this.isMinQueryM ? '20vw' : 0,
      y: this.isMinQueryM ? 0 : '-20vh',
      scale: 1.3,
      opacity: 0,
    };

    // -------------------------- heroTransition -------------------------- //

    // outro heroTextElements
    $heroTextElements.forEach(($element, i) => {
      animations.push(gsap.fromTo($element, {
        y: 0,
        opacity: 1,
      }, {
        scrollTrigger: {
          trigger: '.homepageScrollSection--heroTransition',
          scrub: SCRUB,
          start: `top bottom-=${i * 15}%`,
          end: '+=50%',
        },
        y: '-10vh',
        opacity: 0,
      }, 0));
    });

    // outro heroMedia
    animations.push(gsap.fromTo($heroMedia, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--heroTransition',
        scrub: SCRUB,
        start: 'top bottom',
        end: '+=100%',
      },
      ...lastHeroMediaValues,
    }, 0));

    // -------------------------- vision -------------------------- //

    // intro white circle
    animations.push(gsap.fromTo($whiteCircle, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--vision',
        scrub: SCRUB,
        start: 'top bottom',
        end: '+=50%',
      },
      r: 0.375 * this.vmin/2,
      opacity: 1,
    }, 0));

    animations.push(gsap.fromTo($orbit3, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--vision',
        scrub: SCRUB,
        start: 'top bottom-=15%',
        end: '+=50%',
      },
      ...this.getCircleValues({scaleRadius: VISION_SCALE_RADIUS}),
      // manually set orbit3 position
      onUpdate() {
        const progress = this.progress() || 0;
        const animatePosition = _this.getOrbit3Position();
        const x = lerp(animatePosition.x, 0, progress);
        const y = lerp(animatePosition.y, 0, progress);
        gsap.set($orbit3, {x, y});
      },
      onStart: () => this.stopOrbit3Animation(),
      onReverseComplete: () => this.startOrbit3Animation(),
    }, 0));

    const $visionButton = document.querySelector('.homepageVisionButton');
    let visionTextWidth = $visionText.offsetWidth;

    const visionScrollTrigger = {
      trigger: '.homepageScrollSection--vision',
      scrub: SCRUB,
      start: 'top top',
      end: 'bottom top',
    };

    const updatePixiScene = (progress) => {
      // manually set pixiScene text x
      const endX = (visionTextWidth + windowWidth * 0.1) * -1;
      const x = lerp(windowWidth, endX, progress);
      pixiScene.setTextX(x);
      // manually set vision button opacity (using fromTo was buggy)
      const alpha = (1 - Math.abs( progress - 0.5 ) * 2) * 4;
      $visionButton.style.opacity = alpha;
    };

    let startPixiScene = () => {
      if (!visionTextWidth) visionTextWidth = $visionText.offsetWidth;
      pixiScene.startAnimation();
      $visionProgressCircle.style.opacity = 1;
    };
    let stopPixiScene = () => {
      pixiScene.stopAnimation();
    };

    animations.push(gsap.fromTo($visionProgressCircle, {
      'stroke-dashoffset': this.visionRadius,
    }, {
      'stroke-dashoffset': 0,
      ease: 'none',
      scrollTrigger: visionScrollTrigger,
      onUpdate() {
        updatePixiScene(this.progress() || 0);
      },
      onEnter: isMinQueryM ? undefined : startPixiScene,
    }));

    // HACK separate rendering techniques for desktop vs mobile
    // desktop technique was buggy on mobile
    if (isMinQueryM) {
      startPixiScene = () => {
        if (!visionTextWidth) visionTextWidth = $visionText.offsetWidth;
        pixiScene.startAnimation();
        $visionProgressCircle.style.opacity = 1;
        pixiScene.showView();
      };
      stopPixiScene = () => {
        pixiScene.stopAnimation();
        pixiScene.hideView();
      };

      animations.push(ScrollTrigger.create({
        ...visionScrollTrigger,
        onEnter() {
          updatePixiScene(0);
          startPixiScene();
        },
        onEnterBack() {
          updatePixiScene(1);
          startPixiScene();
        },
        onLeave() {
          updatePixiScene(1);
          stopPixiScene();
        },
        onLeaveBack() {
          updatePixiScene(0);
          stopPixiScene();
        },
      }));
    }

    // -------------------------- teamInterstitial -------------------------- //

    const collisionPosition = {
      x: '-25vw',
      y: '0vh',
    };

    animations.push(gsap.fromTo($whiteCircle, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--teamInterstitial',
        scrub: SCRUB,
        start: 'top top',
        end: '+=50%',
      },
      ...collisionPosition,
      r: 0.5 * this.vmin/2,
      onStart: () => {
        $visionProgressCircle.style.opacity = 0;
        $orbit3.style.stroke = '#FFF';
      },
      onReverseComplete: () => {
        $visionProgressCircle.style.opacity = 1;
        $orbit3.style.stroke = GRAY_5;
      },
    }, 0));

    [$orbit3, $orbit2, $orbit1].forEach(($orbit, i) => {
      animations.push(gsap.fromTo($orbit, {}, {
        scrollTrigger: {
          trigger: '.homepageScrollSection--teamInterstitial',
          scrub: SCRUB,
          start: `top top-=${(i + 1) * 8}%`,
          end: '+=50%',
        },
        ...this.getCircleValues({
          ...collisionPosition,
          scaleRadius: 0.5,
          stroke: '#FFF',
        }),
      }));
    });

    animations.push(gsap.fromTo($teamInterstitialTitle, {
      x: '20vw',
      opacity: 0,
      scale: 1,
    }, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--teamInterstitial',
        scrub: SCRUB,
        start: 'top top-=30%',
        end: '+=50%',
      },
      x: 0,
      opacity: 1,
      scale: 1,
    }, 0));

    // -------------------------- Team -------------------------- //

    animations.push(gsap.fromTo($whiteCircle, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--teamIntro',
        scrub: SCRUB,
        start: 'top top+=20%',
        end: '+=100%',
      },
      x: 0,
      y: 0,
      r: diagonalScale/2 * this.vmin/2,
      opacity: 1,
    }, 0));

    animations.push(gsap.fromTo($teamInterstitialTitle, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--teamIntro',
        scrub: SCRUB,
        start: 'top top+=20%',
        end: '+=50%',
      },
      x: '20vw',
      opacity: 0,
      scale: 2,
    }, 0));

    const teamIntroScrollTrigger = {
      trigger: '.homepageScrollSection--teamIntro',
      scrub: SCRUB,
      start: 'top top-=40%',
      end: '+=50%',
    };

    const teamOrbitValues = this.getTeamOrbitValues();

    const teamOrbitStartValues = {
      ...teamOrbitValues,
      scaleRadius: 1,
    };

    animations.push(gsap.fromTo($orbit1, this.getCircleValues(teamOrbitStartValues), {
      scrollTrigger: teamIntroScrollTrigger,
      ...this.getCircleValues({
        ...teamOrbitValues,
        scaleRadius: isMinQueryM ? 2.8 : 3.5,
        stroke: GRAY_2,
      }),
    }, 0));
    animations.push(gsap.fromTo($orbit2, this.getCircleValues(teamOrbitStartValues), {
      scrollTrigger: {
        ...teamIntroScrollTrigger,
        start: 'top top-=50%',
      },
      ...this.getTeamOrbit2Values(),
      stroke: GRAY_2,
    }, 0));
    animations.push(gsap.fromTo($orbit3, this.getCircleValues(teamOrbitStartValues), {
      scrollTrigger: {
        ...teamIntroScrollTrigger,
        start: 'top top-=60%',
      },
      ...this.getCircleValues({
        ...teamOrbitValues,
        scaleRadius: 1.75,
        stroke: GRAY_2,
      }),
    }, 0));

    animations.push(gsap.fromTo($blackCircle, {}, {
      scrollTrigger: {
        ...teamIntroScrollTrigger,
        start: 'top top-=70%',
      },
      ...this.getCircleValues({
        y: teamOrbitValues.y,
        scaleRadius: 1.5,
        blur: 15,
      }),
    }, 0));

    // -------------------------- Team members -------------------------- //


    animations.push(gsap.fromTo($teamContainer, {
      opacity: 0,
    }, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--team',
        scrub: SCRUB,
        start: 'top top',
        end: '+=50%',
      },
      opacity: 1,
    }, 0));

    // go from 0.95 to 0.55, centered at 0.75
    const teamProgressDelta = 0.2;

    animations.push(gsap.fromTo($teamProgressCircle, {
      'stroke-dashoffset': this.teamRadius * (0.75 + teamProgressDelta),
    }, {
      'stroke-dashoffset': this.teamRadius * (0.75 - teamProgressDelta),
      ease: 'none',
      scrollTrigger: {
        trigger: '.homepageScrollSection--team',
        scrub: SCRUB,
        start: 'top top',
        end: 'bottom bottom',
      },
      onStart() {
        $teamProgressCircle.style.opacity = 1;
      },
      onReverseComplete() {
        $teamProgressCircle.style.opacity = 0;
      },
    }));

    // outro container
    animations.push(gsap.fromTo($teamContainer, {}, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--team',
        scrub: SCRUB,
        start: 'bottom bottom+=50%',
        end: 'bottom bottom',
      },
      opacity: 0,
    }, 0));

    // -------------------------- Investments Interstitial -------------------------- //

    const investmentsInterstitialScrollTrigger = {
      trigger: '.homepageScrollSection--investmentsInterstitial',
      scrub: SCRUB,
      start: 'top bottom',
      end: '+=50%',
    };

    animations.push(gsap.fromTo($blackCircle, {}, {
      scrollTrigger: investmentsInterstitialScrollTrigger,
      ...this.getCircleValues({
        scaleRadius: isMinQueryM ? 0.25 : 0.5,
        blur: 0,
        y: '50vh',
      }),
    }, 0));

    animations.push(gsap.fromTo($orbit3, {}, {
      scrollTrigger: {
        ...investmentsInterstitialScrollTrigger,
        start: 'top bottom-=8%',
      },
      ...this.getInvestmentsOrbitValues(2),
    }, 0));
    animations.push(gsap.fromTo($orbit2, {}, {
      scrollTrigger: {
        ...investmentsInterstitialScrollTrigger,
        start: 'top bottom-=16%',
      },
      ...this.getInvestmentsOrbitValues(1),
      onStart() {
        $teamProgressCircle.style.opacity = 0;
        $orbit2.style.stroke = '#000';
      },
      onReverseComplete() {
        $teamProgressCircle.style.opacity = 1;
        $orbit2.style.stroke = GRAY_2;
      },
    }, 0));
    animations.push(gsap.fromTo($orbit1, {}, {
      scrollTrigger: {
        ...investmentsInterstitialScrollTrigger,
        start: 'top bottom-=24%',
      },
      ...this.getInvestmentsOrbitValues(0),
    }, 0));

    animations.push(gsap.fromTo($orbit2, {}, {
      scrollTrigger: {
        ...investmentsInterstitialScrollTrigger,
        start: 'top bottom-=16%', // with $orbitSvg2
      },
      stroke: GRAY_2,
    }, 0));

    animations.push(gsap.fromTo($investmentsTitle, {
      opacity: 0,
    }, {
      scrollTrigger: {
        ...investmentsInterstitialScrollTrigger,
        start: 'top bottom-=32%',
      },
      opacity: 1,
    }, 0));

    // -------------------------- Investments -------------------------- //

    const investmentsTitleTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.homepageScrollSection--investments',
        scrub: SCRUB,
        start: 'top bottom',
        end: 'top center',
      },
      ease: 'none',
    });
    animations.push(investmentsTitleTimeline);

    investmentsTitleTimeline.fromTo($investmentsTitle, {}, {
      scale: .5,
      y: '-30vh',
    }, 0);

    investmentsTitleTimeline.fromTo($investmentsButton, {
      opacity: 0,
    }, {
      opacity: 1,
    }, 0);

    const scrollSectionInvestmentsHeight = $scrollSectionInvestments.offsetHeight;

    $investmentsProgressCircles.forEach(($progressCircle, i) => {
      const stagger = 0.3;
      const end = 1 - 2 * stagger; // 2 = length - 1

      const startOffset = this.investmentsOrbitScales[i] * this.vmin/2 * Math.PI;

      animations.push(gsap.fromTo($progressCircle, {
        'stroke-dashoffset': startOffset,
      }, {
        'stroke-dashoffset': 0,
        ease: 'none',
        scrollTrigger: {
          trigger: '.homepageScrollSection--investments',
          scrub: SCRUB,
          start: `top bottom-=${(2 - i) * scrollSectionInvestmentsHeight * stagger}px`,
          end: `+=${scrollSectionInvestmentsHeight * end}`,
        },
        onStart() {
          $progressCircle.style.opacity = 1;
          // pre-load ThreeJS scene
          testimonialScene.initialize();
        },
        onReverseComplete() {
          $progressCircle.style.opacity = 0;
        },
      }));

      // HACK reset dashoffset
      $progressCircle.style['stroke-dashoffset'] = startOffset;
    });

    const investmentsOutroScrollTrigger = {
      trigger: '.homepageScrollSection--investments',
      scrub: SCRUB,
      start: 'bottom bottom',
      end: '+=50%',
    };

    const investmentsOutroTimeline = gsap.timeline({
      scrollTrigger: investmentsOutroScrollTrigger,
    });
    animations.push(investmentsOutroTimeline);

    // fade out this stuff
    investmentsOutroTimeline.fromTo([
      $investmentsTitle,
      $investmentsButton,
    ], {}, {
      opacity: 0,
    }, 0);

    animations.push(gsap.fromTo($orbit3, {}, {
      scrollTrigger: {
        ...investmentsOutroScrollTrigger,
        end: '+=60%',
      },
      opacity: 0,
    }, 0));

    animations.push(gsap.fromTo($orbit1, {}, {
      scrollTrigger: {
        ...investmentsOutroScrollTrigger,
        start: 'bottom bottom+=10%',
        end: '+=100%',
      },
      ...this.getCircleValues({
        scaleRadius: 3,
        x: isMinQueryM ? '15vw' : '80vw',
        y: isMinQueryM ? '-75vh' : '30vh',
        stroke: GRAY_5,
      }),
      onStart() {
        gsap.set($investmentsProgressCircles, {opacity: 0});
      },
      onReverseComplete() {
        gsap.set($investmentsProgressCircles, {opacity: 1});
      },
    }, 0));

    animations.push(gsap.fromTo($orbit2, {}, {
      scrollTrigger: {
        ...investmentsOutroScrollTrigger,
        start: 'bottom bottom',
        end: '+=100%',
      },
      ...this.getCircleValues({
        x: isMinQueryM ? '25vw' : '40vw',
        y: isMinQueryM ? '0vh' : '50vh',
        scaleRadius: 2,
        stroke: GRAY_5,
      }),
    }, 0));

    animations.push(gsap.fromTo($blackCircle, {}, {
      scrollTrigger: {
        ...investmentsOutroScrollTrigger,
        start: 'bottom bottom-=10%',
        end: '+=140%',
      },
      ...this.getCircleValues({
        x: '25vw',
        y: '0vh',
        scaleRadius: diagonalScale * 1.5,
      }),
    }, 0));

    // -------------------------- Testimonials -------------------------- //

    const $testimonialMask = document.querySelector('.homepageTestimonialMask');

    $testimonials.forEach(($testimonial, i) => {
      // set mask opacity
      let onEnter; let onEnterBack; let onLeave; let onLeaveBack;
      const revealMask = () => $testimonialMask.style.opacity = 1;
      const hideMask = () => $testimonialMask.style.opacity = 0;
      if (isMinQueryM && i === 0) {
        onEnter = revealMask;
        onLeaveBack = hideMask;
      } else if (isMinQueryM && i === $testimonials.length - 1) {
        onLeave = hideMask;
        onEnterBack = revealMask;
      }

      ScrollTrigger.create({
        trigger: '.homepageScrollSection--testimonial',
        scrub: SCRUB,
        start: `top top-=${i * TESTIMONIAL_ITEM_DISTANCE}px`,
        end: `+=${TESTIMONIAL_SCROLL_DISTANCE}`,
        onUpdate({progress}) {
          const angleX = (progress - 0.5) * 2 * -70;
          const angleZ = (progress - 0.5) * 2 * 15;
          // const angleZ = 0;
          $testimonial.style.transform = `rotateX(${angleX}deg) rotateZ(${angleZ}deg)`;
          $testimonial.style.opacity = Math.sin( progress * Math.PI );
        },
        onEnter,
        onEnterBack,
        onLeave,
        onLeaveBack,
      });
    });

    ScrollTrigger.create({
      trigger: '.homepageScrollSection--testimonial',
      scrub: SCRUB,
      start: 'top top',
      end: 'bottom top',
      onUpdate({progress}) {
        // ease progress so it appears early and leaves late
        const keyframeA = 0.2;
        const keyframeB = 0.8;
        const positionA = 0.4;
        const positionB = 0.6;
        let x; let y; let easeProgress;
        if (progress < keyframeA ) {
          easeProgress = lerp(0, positionA, progress / keyframeA);
        } else if (progress < keyframeB) {
          const alpha = (progress - keyframeA) / (keyframeB - keyframeA);
          easeProgress = lerp(positionA, positionB, alpha);
        } else {
          const alpha = (progress - keyframeB) / (1 - keyframeB);
          easeProgress = lerp(positionB, 1, alpha);
        }
        if (isMinQueryM) {
          x = windowWidth * -0.25;
          // y axis reversed in Three.js world
          y = lerp(-1.2, 1.2, easeProgress) * windowHeight;
          testimonialScene.setModelsPosition(x, y);
        } else {
          x = lerp(-1.6, 1.6, easeProgress) * windowWidth;
          y = windowHeight * 0.35;
        }
        testimonialScene.setModelsPosition(x, y);
      },
      onEnter: () => testimonialScene.startAnimation(),
      onEnterBack: () => testimonialScene.startAnimation(),
      onLeave: () => testimonialScene.stopAnimation(),
      onLeaveBack: () => testimonialScene.stopAnimation(),
    });

    const testimonialOutroScrollTrigger = {
      trigger: '.homepageScrollSection--testimonial',
      scrub: SCRUB,
      start: 'bottom top+=70%',
      end: '+=50%',
    };

    animations.push(gsap.fromTo($orbit1, {}, {
      scrollTrigger: testimonialOutroScrollTrigger,
      ...this.getCircleValues({
        x: isMinQueryM ? '25vw' : '90vw',
        y: isMinQueryM ? '100vh' : '40vh',
        scaleRadius: 3,
      }),
    }, 0));

    animations.push(gsap.fromTo($orbit2, {}, {
      scrollTrigger: {
        ...testimonialOutroScrollTrigger,
        start: 'bottom top+=50%',
      },
      ...this.getCircleValues({
        x: isMinQueryM ? '40vw' : '80vw',
        y: isMinQueryM ? '95vh' : '70vh',
        scaleRadius: 2,
      }),
    }, 0));

    // -------------------------- Media -------------------------- //

    animations.push(gsap.fromTo($mediaButton, {
      opacity: 0,
    }, {
      opacity: 1,
      scrollTrigger: {
        trigger: '.homepageScrollSection--media',
        scrub: SCRUB,
        start: 'top top-=50%',
        end: '+=50%',
      },
    }));

    animations.push(gsap.fromTo($mediaButton, {}, {
      opacity: 0,
      scrollTrigger: {
        trigger: '.homepageScrollSection--media',
        scrub: SCRUB,
        start: 'bottom bottom+=100%',
        end: '+=50%',
      },
    }));

    // -------------------------- Final -------------------------- //

    animations.push(ScrollTrigger.create({
      trigger: '.homepageScrollSection--media',
      scrub: SCRUB,
      start: 'bottom bottom+=100%',
      end: 'bottom bottom',
      onEnter() {
        // reset background element in-prep for the loop
        gsap.set($heroMedia, {
          ...initialHeroMediaValues,
          opacity: 0,
        });
        gsap.set($keyhole, {
          ...initialKeyholeValues,
          display: 'flex',
          opacity: 0,
        });
      },
      onLeaveBack() {
        // reset the reset if scrolling backwards
        gsap.set($heroMedia, lastHeroMediaValues);
        gsap.set($keyhole, {
          ...lastKeyholeValues,
          display: 'none',
          opacity: 1,
        });
      },
      onUpdate({progress}) {
        const radius = windowDiagonal * 0.4;
        const startAngle = 7/8 * Math.PI * 2;
        const endAngle = 5/8 * Math.PI * 2;
        const offsetX = Math.cos(endAngle) * radius;
        const offsetY = Math.sin(endAngle) * radius;
        const theta = lerp(startAngle, endAngle, progress);
        const x = Math.cos(theta) * radius - offsetX;
        const y = Math.sin(theta) * radius - offsetY;

        gsap.set($keyhole, {
          x,
          y,
          scale: lerp(0.1, 1, progress),
          // fade in keyhole first, to avoid see-thru
          opacity: progress * 3 - 1,
        });

        gsap.set($heroMedia, {
          x,
          y,
          scale: lerp(0.1, initialHeroMediaValues.scale, progress),
          // fade in hero media 2nd, to avoid see-thru
          opacity: progress * 3 - 2,
        });
      },
    }));

    const finalOutroScrollTrigger = {
      trigger: '.homepageScrollSection--final',
      scrub: SCRUB,
      start: 'bottom bottom+=80%',
      end: '+=50%',
    };

    animations.push(gsap.fromTo($orbit1, {}, {
      scrollTrigger: finalOutroScrollTrigger,
      ...this.getCircleValues(orbit1InitValues),
    }, 0));
    animations.push(gsap.fromTo($orbit2, {}, {
      scrollTrigger: {
        ...finalOutroScrollTrigger,
        start: 'bottom bottom+=70%',
      },
      ...this.getCircleValues(orbit2InitValues),
    }, 0));
    animations.push(gsap.fromTo($orbit3, {}, {
      scrollTrigger: {
        ...finalOutroScrollTrigger,
        start: 'bottom bottom+=60%',
      },
      ...this.getCircleValues(orbit3InitValues),
      // manually set position
      onUpdate() {
        const progress = this.progress() || 0;
        if (progress) _this.stopOrbit3Animation();
        const animatePosition = _this.getOrbit3Position();
        // position from investmentsInterOrbitValues
        const x = lerp(0, animatePosition.x, progress);
        const y = lerp(windowHeight * 0.5, animatePosition.y, progress);
        gsap.set($orbit3, {x, y});
      },
      onComplete: () => {
        this.startOrbit3Animation();
      },
    }, 0));
    animations.push(gsap.fromTo($orbit3, {}, {
      scrollTrigger: {
        ...finalOutroScrollTrigger,
        start: 'bottom bottom+=60%',
      },
      opacity: 1,
    }, 0));

    animations.push(gsap.fromTo('.site-footer', {
      y: '200px',
    }, {
      scrollTrigger: {
        trigger: '.homepageScrollSection--media',
        scrub: SCRUB,
        start: 'bottom bottom+=200px',
        end: 'bottom bottom',
      },
      y: '0px',
    }, 0));

    // -------------------------- initialize -------------------------- //

    this.createResizeAnimations();

    // set initial values
    gsap.set($blackCircle, initialBlackCircleValues);
    gsap.set($whiteCircle, initialWhiteCircleValues);

    gsap.set($orbit1, this.getCircleValues(orbit1InitValues));
    gsap.set($orbit2, this.getCircleValues(orbit2InitValues));
    gsap.set($orbit3, this.getCircleValues(orbit3InitValues));
    gsap.set($heroTextElements, {opacity: 0});

    // start orbit 3 animation if vision not scrolled in yet
    const $visionScrollSection = document.querySelector('.homepageScrollSection--vision');
    const visionY = $visionScrollSection.getBoundingClientRect().y;
    const isVisionVisible = visionY <= this.windowHeight;
    if (!isVisionVisible) this.startOrbit3Animation();

    window.addEventListener('scroll', this.onScroll);

    document.body.classList.remove('isInitializing');
  }

  onScroll() {
    const scrollDistance = this.bodyHeight - this.windowHeight;
    const {scrollY} = window;
    // update minimap
    const minimapAngle = scrollY / scrollDistance * 360;
    this.$minimapOuter.style.transform = `rotate3d(0, 0, 1, ${minimapAngle}deg)`;
    // loop forward
    if (!this.isMinQueryM) return; // desktop only

    const isAtEnd = scrollDistance - scrollY <= 4;
    if (!isAtEnd) return;
    // reset to top when bottom reached
    this.killAnimations();
    window.scrollTo({top: 0, behavior: 'instant'});
    // re-create all tweens & timelines
    this.createAnimations();
  }

  /**
   * create motion path animations
   */
  createResizeAnimations() {
    // ------ team members ------ //

    this.$teamMembers.forEach(($member, i) => {
      const $card = $member.querySelector('.homepageTeamMember__card');
      const tween = gsap.to($member, {
        ease: 'none',
        motionPath: {
          path: '.homepageSemiCircle path',
          align: '.homepageSemiCircle path',
        },
        scrollTrigger: {
          trigger: '.homepageScrollSection--team',
          scrub: SCRUB,
          start: `top top-=${i * TEAM_MEMBER_DISTANCE}px`,
          end: `+=${TEAM_SCROLL_DISTANCE}`,
        },
        onUpdate() {
          const progress = this.progress() || 0;
          const alpha = Math.cos(progress * Math.PI * 2) * -1.5;
          $card.style.opacity = alpha;
        },
      });
      this.resizeAnimations.push(tween);
    });

    // ------ investments ------ //

    [
      {$items: this.$investmentItemsTop, modifier: 'top'},
      {$items: this.$investmentItemsBottom, modifier: 'bottom'},
    ].forEach(({$items, modifier}) => {
      $items.forEach(($item, i) => {
        const path = `.homepageInvestmentsPath--${modifier} path`;
        const tween = gsap.to($item, {
          ease: 'none',
          motionPath: {
            path,
            align: path,
            alignOrigin: [0.5, 0.5],
          },
          scrollTrigger: {
            trigger: '.homepageScrollSection--investments',
            scrub: SCRUB,
            start: `top bottom-=${i * INVESTMENTS_ITEM_DISTANCE}px`,
            end: `+=${INVESTMENTS_SCROLL_DISTANCE}`,
          },
        });
        this.animations.push(tween);
        this.resizeAnimations.push(tween);
      });
    });

    // ------ media ------ //

    this.$mediaCards.forEach(($mediaCard, i) => {
      const tween = gsap.fromTo($mediaCard, {
        scale: 0.2,
      }, {
        ease: 'none',
        motionPath: {
          path: '.homepageSemiCircleMedia path',
          align: '.homepageSemiCircleMedia path',
          alignOrigin: [.5, .65],
        },
        scale: 1,
        scrollTrigger: {
          trigger: '.homepageScrollSection--media',
          scrub: SCRUB,
          // -2 = start a bit early
          start: `top top-=${(i - 2) * MEDIA_ITEM_DISTANCE}%`,
          end: `+=${MEDIA_SCROLL_DISTANCE}%`,
        },
        // set opacity to fade out quick
        onUpdate() {
          const progress = this.progress() || 0;
          // hide on beginning and end
          // fixes bug from displaying when initialized
          $mediaCard.style.opacity = progress === 1 ? 0 : 1.5 - ( 1 - progress) * 2.5;
        },
      });
      this.animations.push(tween);
      this.resizeAnimations.push(tween);
    });
  }


  updateMeasurements() {
    const rulerStyle = getComputedStyle(this.$ruler);
    this.windowWidth = parseInt( rulerStyle.getPropertyValue('width') );
    this.windowHeight = parseInt( rulerStyle.getPropertyValue('height') );
    this.vmin = Math.min(this.windowWidth, this.windowHeight);
    this.windowDiagonal = getDistance(this.windowWidth, this.windowHeight);
    this.diagonalScale = this.windowDiagonal / this.vmin * 2.5;
    this.isMinQueryM = this.windowWidth >= 769;
    this.bodyHeight = document.body.offsetHeight;
    this.updateShapesPosition();
  }

  updateShapesPosition() {
    this.$shapesSvg.setAttribute('width', this.windowWidth);
    this.$shapesSvg.setAttribute('height', this.windowHeight);
    const viewX = Math.round(this.windowWidth/2) * -1;
    const viewY = Math.round(this.windowHeight/2) * -1;
    this.$shapesSvg.setAttribute('viewBox', `${viewX} ${viewY} ${this.windowWidth} ${this.windowHeight}`);

    this.investmentsOrbitScales = [
      this.isMinQueryM ? 1 : 2,
      this.isMinQueryM ? 0.75 : 1.5,
      this.isMinQueryM ? 0.5 : 1,
    ];

    this.visionRadius = Math.round(this.vmin/2 * VISION_SCALE_RADIUS * Math.PI * 2);
    this.teamOrbit2Scale = this.isMinQueryM ? 2.2 : 2.4;
    this.teamRadius = Math.round(this.vmin/2 * Math.PI * 2 * this.teamOrbit2Scale);
    this.$visionProgressCircle.style.strokeDasharray = this.visionRadius;
    const visionR = this.getCircleValues({scaleRadius: VISION_SCALE_RADIUS}).r;
    this.$visionProgressCircle.setAttribute('r', visionR);

    gsap.set(this.$teamProgressCircle, {
      ...this.getTeamOrbit2Values(),
      'stroke-dasharray': this.teamRadius,
    });

    this.$investmentsProgressCircles.forEach(($circle, i) => {
      const scale = this.investmentsOrbitScales[i];
      $circle.style.strokeDasharray = Math.round(scale * this.vmin/2 * Math.PI * 2);
      gsap.set($circle, this.getInvestmentsOrbitValues(i));
    });
  }

  getCircleValues(values) {
    const circleValues = {...values};
    const {x, y, scaleRadius} = circleValues;
    const isXVW = x !== undefined && typeof x === 'string' && x.endsWith('vw');
    const isYVH = y !== undefined && typeof y === 'string' && y.endsWith('vh');

    if (isXVW) {
      const x = parseInt(circleValues.x) / 100;
      circleValues.x = x * this.windowWidth;
    }
    if (isYVH) {
      const y = parseInt(circleValues.y) / 100;
      circleValues.y = y * this.windowHeight;
    }
    if (scaleRadius) {
      circleValues.r = scaleRadius * this.vmin /2;
      delete circleValues.scaleRadius;
    }
    // disable blur on mobile
    if (circleValues.blur !== undefined && !this.isMinQueryM) {
      delete circleValues.blur;
    }
    return circleValues;
  }

  getTeamOrbitValues() {
    return {
      x: '0vw',
      y: this.isMinQueryM ? '100vh' : '75vh',
    };
  }

  getTeamOrbit2Values() {
    return this.getCircleValues({
      ...this.getTeamOrbitValues(),
      scaleRadius: this.teamOrbit2Scale,
    });
  }

  getInvestmentsOrbitValues(index) {
    return this.getCircleValues({
      x: '0vw',
      y: '50vh',
      scaleRadius: this.investmentsOrbitScales[index],
    });
  }

  onResize() {
    // do not trigger resize on phones showing/hiding toolbars
    const isMobileToolbar = window.innerWidth === this.windowWidth &&
      window.innerWidth < 601;
    if (isMobileToolbar) return;

    this.updateMeasurements();
    // reset animations that need to be reset on resize
    this.killResizeAnimations();
    this.createResizeAnimations();
  }

  killAnimations() {
    this.animations.forEach((animation) => animation.kill());
    this.animations = [];
    this.pixiScene.stopAnimation();
    window.removeEventListener('scroll', this.onScroll);
  }

  killResizeAnimations() {
    this.resizeAnimations.forEach((animation) => animation.kill());
    // remove old resizeAnimations from animations
    this.animations.filter((animation) => {
      return !this.resizeAnimations.includes(animation);
    });
    this.resizeAnimations = [];
  }

  unmount() {
    this.killAnimations();

    window.removeEventListener('resize', this.onResize);

    this.sectionSelector.unmount();
    this.pixiScene.unmount();
    this.testimonialScene.unmount();
  }
}
